import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faSquareCheck,
  IconDefinition,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { searchIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { process } from '@progress/kendo-data-query';
import { AbstractComponent } from 'src/app/shared/components/abstract-component';
import { DashboardService } from '../../services/dashboard.service';
import { AllNotificationsResponse } from '../../interface/get-all-notifications-response';
import { debounceTime, distinctUntilChanged, Subject, take, takeUntil } from 'rxjs';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  allNotificationsPageSize,
  NotificationAction,
  NotificationReadAllBit,
} from 'src/app/shared/enums/notification';
import { orderBy } from 'lodash';
import { MarkPayload } from '../../interface/mark-payload';
import { formatNotificationDateTime } from 'src/app/shared/functions/format-notifications-datetime';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import _ from 'lodash';
import { MarkReadNotificationService } from '../../services/notification.service';
import { dialogTimer } from 'src/app/shared/constants/dialog-specs';
import { MarkingWarnService } from '../../services/marking-warn.service';
import { DialogCloseResult } from '@progress/kendo-angular-dialog';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  standalone: false,
})
export class NotificationComponent extends AbstractComponent implements OnInit {
  squareCheck: SVGIcon = this.customFaIcon(faSquareCheck as IconDefinition);
  readonly defaultTotal: number = 0;
  constructor(
    library: FaIconLibrary,
    private dashboardService: DashboardService,
    private notificationService: NotificationService,
    private markReadNotificationService: MarkReadNotificationService,
    private markingWarnService: MarkingWarnService,
    private vcr: ViewContainerRef
  ) {
    super();
    library.addIcons(faSearch);
  }
  public searchIcon: SVGIcon = searchIcon;
  public pageSize: number = allNotificationsPageSize;
  public gridView: GridDataResult;
  public searchValue: string = '';
   private searchInput$ = new Subject<string>();
  public allNotifications: AllNotificationsResponse[] = [];
  public loadSpinner: boolean = false;
  skip: number = 0;
  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,
      content: `<path d="${icon.icon[4]}" />`,
      viewBox: `0 0 ${icon.icon[0]} ${icon.icon[1]}`,
    };
  }

  ngOnInit(): void {
    this.fetchAllNotifications();
    // Once a notification is marked as read, start polling again to refresh the list.
    this.searchInput$
      .pipe(
        debounceTime(300),          // wait 300ms after typing stops
        distinctUntilChanged(),      // only fire if value changed
        takeUntil(this.componentDestroyed)
      )
      .subscribe((value) => {
        this.searchValue = value?.trim() || '';
        this.fetchAllNotifications(this.searchValue);
      });
   this.markReadNotificationService.notificationMarked$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((isView) => {
        if(!isView)
          this.fetchAllNotifications(this.searchValue);
      });
  }

  fetchAllNotifications(nycId: string = ''): void {
        const pageNumber: number = Math.floor(this.skip / this.pageSize);
    this.loadSpinner = true;   
    this.dashboardService
      .getAllNotifications(pageNumber, this.pageSize, nycId)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe({
        next: (response: AllNotificationsResponse[]) => {
          this.loadSpinner = false;
          this.allNotifications = orderBy(
            response || [],
            ['dateTime'],
            ['desc']
          );

          // Format dateTime for each notification item
          response.forEach((item: AllNotificationsResponse) => {
            item.dateTime = formatNotificationDateTime(
              new Date(item.dateTime), // Date/time from the backend
              new Date() // todays Date
            );
          });
          this.gridView =
          {
          data: this.allNotifications,
          total: _.first(response)?.totalCount ?? this.defaultTotal,
          }; 
        },
        error: () => {
          this.loadSpinner = false;
          this.notificationService.show({
            content: 'Fetching notifications failed. Please try again later.',
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

  markAllAsDoneOrRead(): void {
    this.loadSpinner = true;
    this.dashboardService
      .markAllRead()
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe({
        next: () => {
          this.loadSpinner = false;
          this.markReadNotificationService.notifyNotificationMarked();
          this.notificationService.show({
            content: 'Marking notifications successful.',
            cssClass: 'notification-success',
            hideAfter: dialogTimer,
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'success', icon: true },
          });
        },
        error: () => {
          this.loadSpinner = false;
          this.notificationService.show({
            content: 'Marking notifications failed. Please try again later.',
            cssClass: 'notification-error',
            position: { horizontal: 'center', vertical: 'top' },
            animation: { type: 'slide', duration: 60 },
            type: { style: 'error', icon: true },
          });
        },
      });
  }

    onPageChange(event: PageChangeEvent): void {
      this.skip = event.skip;
      this.pageSize = event.take;
      this.fetchAllNotifications(this.searchValue); // fetch new page from backend
    }

  public handleNotificationSearch(id: string): void {
    // this.searchValue = id;
    // this.fetchAllNotifications(id);
     this.searchInput$.next(id?.trim());
  }

  showAllReadWarning(): void {
    this.markingWarnService
            .open('markAll', this.vcr)
             .pipe(take(1))
            .subscribe((result: DialogCloseResult | any) => {
              if (!(result instanceof DialogCloseResult)) {
                this.markAllAsDoneOrRead();
              }
            });
  }

  override afterOnDestroy(): void {}
}
